import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { PrnService } from './prn-moduel.service'; // adjust path as needed
import { HttpResponse } from '@angular/common/http';
import { ConfirmDialogService } from '../confirm-dialog/confirm-dialog.service';
import { PaginationComponent } from '../Common/pagination/pagination.component';

@Component({
  selector: 'app-prn',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, PaginationComponent],
  templateUrl: './prn-module.component.html',
  styleUrls: ['./prn-module.component.scss'],
})
export class PrnComponent implements OnInit {
  private prnService = inject(PrnService);
  private toastr = inject(ToastrService);
  private router = inject(Router);
  private confirmDialog = inject(ConfirmDialogService);

  /** PRN ids with an in-flight dispatch call. */
  dispatching = new Set<string>();

  currentPage = 1;
  pageSize = 10;
  prnList: any[] = [];
  prnsLoading = false;
  pageCount = 1;
  searchTerm = '';

  // Example form data for creating PRN
  prnForm: any = {
    prnNumber: '',
    purchaseOrderId: '',
    vendorId: '',
    invoiceNumber: '',
    invoiceDate: '',
    returnDate: '',
    subTotalAmount: 0,
    schemeDiscountAmount: 0,
    fixedDiscountAmount: 0,
    overallDiscountAmount: 0,
    gstAmount: 0,
    cessAmount: 0,
    otherCharges: 0,
    freightCharges: 0,
    packingCharges: 0,
    roundOffAmount: 0,
    totalAmount: 0,
    totalQty: 0,
    totalItems: 0,
    remarks: '',
    status: 'CREATED', // default status
    items: [
      {
        productVariantId: '',
        skuCode: '',
        uom: '',
        returnedQuantity: 0,
        actualCostPrice: 0,
        gstPercent: 0,
        gstAmount: 0,
        cessRate: 0,
        cessAmount: 0,
        totalPrice: 0,
        remarks: '',
      },
    ],
  };

  ngOnInit(): void {
    this.loadPrns();
  }

  loadPrns() {
    this.prnsLoading = true;
    this.prnService
      .getPrns(
        { page: this.currentPage, limit: this.pageSize },
        this.searchTerm
      )
      .subscribe((res: any) => {
        this.prnList = res.data;
        this.pageCount = res.pageCount;
        this.prnsLoading = false;
      });
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.pageCount) {
      this.currentPage = page;
      this.loadPrns();
    }
  }

  onPageSizeChange(limit: number) {
    this.pageSize = limit;
    this.pageSizeChange();
  }

  pageSizeChange() {
    this.currentPage = 1;
    this.loadPrns();
  }

  onSearch() {
    this.currentPage = 1;
    this.loadPrns();
  }


  private extractFilename(response: HttpResponse<Blob>, poId: string): string {
    const disposition = response.headers.get('Content-Disposition') || '';
    let match = disposition.match(/filename\*=([^;]+)/i);
    if (match && match[1]) {
      return decodeURIComponent(match[1].split("''").pop() || '');
    }
    match = disposition.match(/filename="?([^";]+)"?/i);
    if (match && match[1]) {
      return match[1];
    }
    return `prn-${poId}.pdf`;
  }

  /**
   * POSTED -> DISPATCHED (goods physically handed to the vendor). Stock was
   * already debited at POSTED, so this only records the handover. Irreversible.
   */
  async markHandedOver(prn: any): Promise<void> {
    if (this.dispatching.has(prn.id)) return;

    const proceed = await this.confirmDialog.confirm({
      title: 'Hand over to vendor?',
      message:
        `Confirm that the goods for <strong>${prn.prnNumber}</strong>` +
        (prn.vendor?.name ? ` (${prn.vendor.name})` : '') +
        ` have been physically handed to the vendor. This cannot be undone.`,
      confirmText: 'Handed over',
      cancelText: 'Cancel',
      size: 'lg',
    });
    if (!proceed) return;

    this.dispatching.add(prn.id);
    this.prnService.dispatchPrn(prn.id).subscribe({
      next: () => {
        this.dispatching.delete(prn.id);
        this.toastr.success(`${prn.prnNumber} marked as handed over`);
        this.loadPrns();
      },
      error: (err) => {
        this.dispatching.delete(prn.id);
        this.toastr.error(
          err?.error?.message || `Failed to mark ${prn.prnNumber} as handed over`
        );
      },
    });
  }

  downloadPrn(id: string) {
    this.prnService
    .downloadPrn(id)
    .subscribe((response: HttpResponse<Blob>) => {
      const blob = response.body as Blob;
      const filename = this.extractFilename(response, id);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    });
  }
}
